import * as Yup from 'yup'
import { useFormik } from 'formik'
import { FC, useState } from 'react'
import { sharedDatePickerOptions } from '../../../Constants/DatepickerOptions'
import { QUERIES } from '../../../Constants/Queries'
import { AwardInfo, AwardsApi, useAuthContext } from '@pull/pull-groupm-csp-api'
import { useMutation, useQueryClient } from 'react-query'
import { useEditModalContext } from '../../../modules/core/functional/EditModalContext'
import Flatpickr from "react-flatpickr"
import clsx from 'clsx'
import { ModalContainer } from '../../../helpers/components/generic/ModalContainer'
import { LoadingComponent } from '../../../modules/core/LoadingComponent'
import { getErrorFromResponse } from '../../../helpers/functions/getErrorFromResponse'
import { Delete, Success } from '../../../helpers/functions/SwalHelpers'
import { ErrorBanner, NotificationBanner, WarningBanner } from '../../../helpers/components/generic/NotificationBanner'
import moment from 'moment'

const editAwardSchema = Yup.object().shape({
	name: Yup.string()
		.required("The name is required"),
	submissionOpeningDate: Yup.date()
		.required("The Submission Opening Date is required!")
	,
	submissionClosingDate: Yup.date()
		.required("The Submission Closing Date is required!")
		.min(Yup.ref("submissionOpeningDate"), "Submission closing date must be after the opening date.")
	,
	judgingOpeningDate: Yup.date()
		.required("The Judging Opening Date is required!")
	,
	judgingClosingDate: Yup.date()
		.required("The Judging Closing Date is required!")
		.min(Yup.ref("judgingOpeningDate"), "Judging closing date must be after the opening date.")
	,
})


const AwardSettingsForm: FC = () => {
	const { getApiConfiguration } = useAuthContext()
	const awardsApi = new AwardsApi(getApiConfiguration())
	const queryClient = useQueryClient()
	const { updateSelectedValue, selectedValue } = useEditModalContext()
	const [error, setError] = useState<string | null>(null)

	const award = selectedValue.info

	const [awardForEdit] = useState({
		id: award.id || 0,
		name: award.name || undefined,
		awardCategoryID: award.awardCategoryID || 0,
		submissionOpeningDate: award.submissionOpeningDate || undefined,
		submissionClosingDate: award.submissionClosingDate || undefined,
		judgingOpeningDate: award.judgingOpeningDate || undefined,
		judgingClosingDate: award.judgingClosingDate || undefined,
		judgingNotificationsDate: award.judgingNotificationsDate ? new Date(award.judgingNotificationsDate).toLocaleString()  : "Not yet sent",
		closed: false
	})

	const cancel = () => {
		updateSelectedValue(null)

	}

	const formik = useFormik({
		initialValues: awardForEdit,
		validationSchema: editAwardSchema,
		onSubmit: async (values, { setSubmitting }) => {
			setSubmitting(true)
			const awardsApi = new AwardsApi(getApiConfiguration())
			try {
				const response = await awardsApi.apiAwardsAwardsPost(values)
				setSubmitting(false)
				if (response) {
					queryClient.invalidateQueries([QUERIES.AWARDS.GET, values.id])
					queryClient.invalidateQueries([QUERIES.AWARDS.GET_CATEGORY + values.awardCategoryID])
					queryClient.invalidateQueries([QUERIES.AWARDS.SEARCH + award.awardCategoryID])
					Success(`The award was successfully ${awardForEdit.id === 0 ? "created" : "updated"}!`)
					cancel()
				}
			} catch (ex) {
				if (ex as Response) {
					const errorMessage = await getErrorFromResponse(ex as Response)
					setError(errorMessage)
				}
				else {
					console.log(ex)
				}
			}
		},
	})

	const deleteOnClick = () => {
		Delete("award")
			.then(async (result) => {
				if (result.isConfirmed) {
					await deleteItem.mutateAsync()
				}
			})
	}

	const deleteItem = useMutation(() => awardsApi.apiAwardsAwardsIdDelete(award.id), {
		// 💡 response of the mutation is passed to onSuccess
		onSuccess: () => {
			// ✅ update detail view directly
			Success("Successfully deleted the award")
			queryClient.invalidateQueries([QUERIES.AWARDS.SEARCH + award.awardCategoryID])
			cancel()

		},
	})



	return (
		<ModalContainer id="kt_modal_submissions">
			<form className="form" onSubmit={formik.handleSubmit} noValidate id="kt_modal_submissions_edit_award_details_form">
				<div className="modal-header" id="kt_modal_submissions_edit_award_details_header">
					<h2>Edit award ({award.name ?? "New Award"})</h2>
					<button onClick={cancel} className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
						<span className="svg-icon svg-icon-1">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="currentColor"></rect>
								<rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="currentColor"></rect>
							</svg>
						</span>
					</button>
				</div>
				<div className="modal-body py-10 px-lg-17" id="kt_modal_submissions_edit_award_details_scroll">
					<div className="scroll-y me-n7 pe-7" data-kt-scroll="true" data-kt-scroll-activate="{default: false, lg: true}" data-kt-scroll-max-height="auto" data-kt-scroll-dependencies="#kt_modal_submissions_edit_award_details_header, #kt_modal_submissions_edit_award_details_footer" data-kt-scroll-wrappers="#kt_modal_submissions_edit_award_details_scroll" data-kt-scroll-offset="50px" style={{ maxHeight: "856px" }}>
						{error &&
							<ErrorBanner message={error} />
						}
						{award.isInSubmissions &&
							<WarningBanner message="This award is in the submission stage, changing it’s dates might affect this status" />
						}
						{award.isInJudging &&
							<WarningBanner message="This award is in the judging stage, changing it’s dates might affect this status" />
						}
						{award.isClosed &&
							<NotificationBanner message="This award is closed." />
						}



						<div className="d-flex flex-column mb-8 fv-row">
							<label className="required fs-5 fw-semibold mb-2" htmlFor="modal_submissions_edit_award_name">Award Name</label>
							<input className={clsx(
								'form-control form-control-solid',
								{ 'is-invalid': formik.touched.name && formik.errors.name },
								{
									'is-valid': formik.touched.name && !formik.errors.name,
								}
							)}
								type='text'
								{...formik.getFieldProps('name')}
								placeholder=""
								autoComplete='off'
								id="modal_submissions_edit_award_name"
								disabled={formik.isSubmitting || award.isClosed} />
							{formik.touched.name && formik.errors.name && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role="alert" >
											<>{formik.errors.name}</>
										</span>
									</div>
								</div>
							)}
						</div>
						<div className="row g-9">
							<div className="col-md-6 fv-row">
								<label className="required fs-6 fw-semibold mb-2" htmlFor="modal_submissions_edit_award_submissions_open">Submissions Opening Date</label>
								<div className="position-relative d-flex align-items-center">
									<span className="svg-icon svg-icon-2 position-absolute mx-4">
										<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="currentColor"></path>
											<path d="M6 6C5.4 6 5 5.6 5 5V3C5 2.4 5.4 2 6 2C6.6 2 7 2.4 7 3V5C7 5.6 6.6 6 6 6ZM11 5V3C11 2.4 10.6 2 10 2C9.4 2 9 2.4 9 3V5C9 5.6 9.4 6 10 6C10.6 6 11 5.6 11 5ZM15 5V3C15 2.4 14.6 2 14 2C13.4 2 13 2.4 13 3V5C13 5.6 13.4 6 14 6C14.6 6 15 5.6 15 5ZM19 5V3C19 2.4 18.6 2 18 2C17.4 2 17 2.4 17 3V5C17 5.6 17.4 6 18 6C18.6 6 19 5.6 19 5Z" fill="currentColor"></path>
											<path d="M8.8 13.1C9.2 13.1 9.5 13 9.7 12.8C9.9 12.6 10.1 12.3 10.1 11.9C10.1 11.6 10 11.3 9.8 11.1C9.6 10.9 9.3 10.8 9 10.8C8.8 10.8 8.59999 10.8 8.39999 10.9C8.19999 11 8.1 11.1 8 11.2C7.9 11.3 7.8 11.4 7.7 11.6C7.6 11.8 7.5 11.9 7.5 12.1C7.5 12.2 7.4 12.2 7.3 12.3C7.2 12.4 7.09999 12.4 6.89999 12.4C6.69999 12.4 6.6 12.3 6.5 12.2C6.4 12.1 6.3 11.9 6.3 11.7C6.3 11.5 6.4 11.3 6.5 11.1C6.6 10.9 6.8 10.7 7 10.5C7.2 10.3 7.49999 10.1 7.89999 10C8.29999 9.90003 8.60001 9.80003 9.10001 9.80003C9.50001 9.80003 9.80001 9.90003 10.1 10C10.4 10.1 10.7 10.3 10.9 10.4C11.1 10.5 11.3 10.8 11.4 11.1C11.5 11.4 11.6 11.6 11.6 11.9C11.6 12.3 11.5 12.6 11.3 12.9C11.1 13.2 10.9 13.5 10.6 13.7C10.9 13.9 11.2 14.1 11.4 14.3C11.6 14.5 11.8 14.7 11.9 15C12 15.3 12.1 15.5 12.1 15.8C12.1 16.2 12 16.5 11.9 16.8C11.8 17.1 11.5 17.4 11.3 17.7C11.1 18 10.7 18.2 10.3 18.3C9.9 18.4 9.5 18.5 9 18.5C8.5 18.5 8.1 18.4 7.7 18.2C7.3 18 7 17.8 6.8 17.6C6.6 17.4 6.4 17.1 6.3 16.8C6.2 16.5 6.10001 16.3 6.10001 16.1C6.10001 15.9 6.2 15.7 6.3 15.6C6.4 15.5 6.6 15.4 6.8 15.4C6.9 15.4 7.00001 15.4 7.10001 15.5C7.20001 15.6 7.3 15.6 7.3 15.7C7.5 16.2 7.7 16.6 8 16.9C8.3 17.2 8.6 17.3 9 17.3C9.2 17.3 9.5 17.2 9.7 17.1C9.9 17 10.1 16.8 10.3 16.6C10.5 16.4 10.5 16.1 10.5 15.8C10.5 15.3 10.4 15 10.1 14.7C9.80001 14.4 9.50001 14.3 9.10001 14.3C9.00001 14.3 8.9 14.3 8.7 14.3C8.5 14.3 8.39999 14.3 8.39999 14.3C8.19999 14.3 7.99999 14.2 7.89999 14.1C7.79999 14 7.7 13.8 7.7 13.7C7.7 13.5 7.79999 13.4 7.89999 13.2C7.99999 13 8.2 13 8.5 13H8.8V13.1ZM15.3 17.5V12.2C14.3 13 13.6 13.3 13.3 13.3C13.1 13.3 13 13.2 12.9 13.1C12.8 13 12.7 12.8 12.7 12.6C12.7 12.4 12.8 12.3 12.9 12.2C13 12.1 13.2 12 13.6 11.8C14.1 11.6 14.5 11.3 14.7 11.1C14.9 10.9 15.2 10.6 15.5 10.3C15.8 10 15.9 9.80003 15.9 9.70003C15.9 9.60003 16.1 9.60004 16.3 9.60004C16.5 9.60004 16.7 9.70003 16.8 9.80003C16.9 9.90003 17 10.2 17 10.5V17.2C17 18 16.7 18.4 16.2 18.4C16 18.4 15.8 18.3 15.6 18.2C15.4 18.1 15.3 17.8 15.3 17.5Z" fill="currentColor"></path>
										</svg>
									</span>
									<Flatpickr data-enable-time {...formik.getFieldProps("submissionOpeningDate")}
										placeholder="Select a date"
										className={clsx(
											'form-control form-control-solid ps-12 mb-lg-0 flatpickr-input',
											{ 'is-invalid': formik.touched.submissionOpeningDate && formik.errors.submissionOpeningDate },
											{
												'is-valid': formik.touched.submissionOpeningDate && !formik.errors.submissionOpeningDate,
											}
										)}
										options={sharedDatePickerOptions}
										value={formik.values.submissionOpeningDate}
										onChange={(val: any) => {
											const date: Date = new Date(val[0])
											if (!isNaN(date.getTime()))
												formik.setFieldValue("submissionOpeningDate", moment(date).format("YYYY-MM-DDTHH:mm:ss"))
											else
												formik.setFieldValue("submissionOpeningDate", null)

										}}
										disabled={formik.isSubmitting || award.isClosed}
									/>
								</div>
							</div>
							<div className="col-md-6 fv-row">
								<label className="required fs-6 fw-semibold mb-2" htmlFor="modal_submissions_edit_award_submissions_close">Submissions Closing Date</label>
								<div className="position-relative d-flex align-items-center">
									<span className="svg-icon svg-icon-2 position-absolute mx-4">
										<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="currentColor"></path>
											<path d="M6 6C5.4 6 5 5.6 5 5V3C5 2.4 5.4 2 6 2C6.6 2 7 2.4 7 3V5C7 5.6 6.6 6 6 6ZM11 5V3C11 2.4 10.6 2 10 2C9.4 2 9 2.4 9 3V5C9 5.6 9.4 6 10 6C10.6 6 11 5.6 11 5ZM15 5V3C15 2.4 14.6 2 14 2C13.4 2 13 2.4 13 3V5C13 5.6 13.4 6 14 6C14.6 6 15 5.6 15 5ZM19 5V3C19 2.4 18.6 2 18 2C17.4 2 17 2.4 17 3V5C17 5.6 17.4 6 18 6C18.6 6 19 5.6 19 5Z" fill="currentColor"></path>
											<path d="M8.8 13.1C9.2 13.1 9.5 13 9.7 12.8C9.9 12.6 10.1 12.3 10.1 11.9C10.1 11.6 10 11.3 9.8 11.1C9.6 10.9 9.3 10.8 9 10.8C8.8 10.8 8.59999 10.8 8.39999 10.9C8.19999 11 8.1 11.1 8 11.2C7.9 11.3 7.8 11.4 7.7 11.6C7.6 11.8 7.5 11.9 7.5 12.1C7.5 12.2 7.4 12.2 7.3 12.3C7.2 12.4 7.09999 12.4 6.89999 12.4C6.69999 12.4 6.6 12.3 6.5 12.2C6.4 12.1 6.3 11.9 6.3 11.7C6.3 11.5 6.4 11.3 6.5 11.1C6.6 10.9 6.8 10.7 7 10.5C7.2 10.3 7.49999 10.1 7.89999 10C8.29999 9.90003 8.60001 9.80003 9.10001 9.80003C9.50001 9.80003 9.80001 9.90003 10.1 10C10.4 10.1 10.7 10.3 10.9 10.4C11.1 10.5 11.3 10.8 11.4 11.1C11.5 11.4 11.6 11.6 11.6 11.9C11.6 12.3 11.5 12.6 11.3 12.9C11.1 13.2 10.9 13.5 10.6 13.7C10.9 13.9 11.2 14.1 11.4 14.3C11.6 14.5 11.8 14.7 11.9 15C12 15.3 12.1 15.5 12.1 15.8C12.1 16.2 12 16.5 11.9 16.8C11.8 17.1 11.5 17.4 11.3 17.7C11.1 18 10.7 18.2 10.3 18.3C9.9 18.4 9.5 18.5 9 18.5C8.5 18.5 8.1 18.4 7.7 18.2C7.3 18 7 17.8 6.8 17.6C6.6 17.4 6.4 17.1 6.3 16.8C6.2 16.5 6.10001 16.3 6.10001 16.1C6.10001 15.9 6.2 15.7 6.3 15.6C6.4 15.5 6.6 15.4 6.8 15.4C6.9 15.4 7.00001 15.4 7.10001 15.5C7.20001 15.6 7.3 15.6 7.3 15.7C7.5 16.2 7.7 16.6 8 16.9C8.3 17.2 8.6 17.3 9 17.3C9.2 17.3 9.5 17.2 9.7 17.1C9.9 17 10.1 16.8 10.3 16.6C10.5 16.4 10.5 16.1 10.5 15.8C10.5 15.3 10.4 15 10.1 14.7C9.80001 14.4 9.50001 14.3 9.10001 14.3C9.00001 14.3 8.9 14.3 8.7 14.3C8.5 14.3 8.39999 14.3 8.39999 14.3C8.19999 14.3 7.99999 14.2 7.89999 14.1C7.79999 14 7.7 13.8 7.7 13.7C7.7 13.5 7.79999 13.4 7.89999 13.2C7.99999 13 8.2 13 8.5 13H8.8V13.1ZM15.3 17.5V12.2C14.3 13 13.6 13.3 13.3 13.3C13.1 13.3 13 13.2 12.9 13.1C12.8 13 12.7 12.8 12.7 12.6C12.7 12.4 12.8 12.3 12.9 12.2C13 12.1 13.2 12 13.6 11.8C14.1 11.6 14.5 11.3 14.7 11.1C14.9 10.9 15.2 10.6 15.5 10.3C15.8 10 15.9 9.80003 15.9 9.70003C15.9 9.60003 16.1 9.60004 16.3 9.60004C16.5 9.60004 16.7 9.70003 16.8 9.80003C16.9 9.90003 17 10.2 17 10.5V17.2C17 18 16.7 18.4 16.2 18.4C16 18.4 15.8 18.3 15.6 18.2C15.4 18.1 15.3 17.8 15.3 17.5Z" fill="currentColor"></path>
										</svg>
									</span>
									<Flatpickr data-enable-time {...formik.getFieldProps("submissionClosingDate")}
										placeholder="Select a date"
										className={clsx(
											'form-control form-control-solid ps-12 mb-lg-0 flatpickr-input',
											{ 'is-invalid': formik.touched.submissionClosingDate && formik.errors.submissionClosingDate },
											{
												'is-valid': formik.touched.submissionClosingDate && !formik.errors.submissionClosingDate,
											}
										)}
										options={sharedDatePickerOptions}
										value={formik.values.submissionClosingDate}
										onChange={(val: any) => {
											const date: Date = new Date(val[0])
											if (!isNaN(date.getTime()))
												formik.setFieldValue("submissionClosingDate", moment(date).format("YYYY-MM-DDTHH:mm:ss"))
											else
												formik.setFieldValue("submissionClosingDate", null)

										}}
										disabled={formik.isSubmitting || award.isClosed}
									/>

								</div>
							</div>
							<div className="col-md-6 fv-row">
								{formik.touched.submissionOpeningDate && formik.errors.submissionOpeningDate && (

									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role="alert" >
												<>{formik.errors.submissionOpeningDate}</>
											</span>
										</div>
									</div>

								)}
							</div>
							<div className="col-md-6 fv-row">
								{formik.touched.submissionClosingDate && formik.errors.submissionClosingDate && (

									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role="alert" >
												<>{formik.errors.submissionClosingDate}</>
											</span>
										</div>
									</div>

								)}
							</div>
						</div>
						<div className="row g-9">
							<div className="col-md-6 fv-row">
								<label className="required fs-6 fw-semibold mb-2" htmlFor="modal_submissions_edit_award_judging_open">Judging Opening Date</label>

								<div className="position-relative d-flex align-items-center">
									<span className="svg-icon svg-icon-2 position-absolute mx-4">
										<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="currentColor"></path>
											<path d="M6 6C5.4 6 5 5.6 5 5V3C5 2.4 5.4 2 6 2C6.6 2 7 2.4 7 3V5C7 5.6 6.6 6 6 6ZM11 5V3C11 2.4 10.6 2 10 2C9.4 2 9 2.4 9 3V5C9 5.6 9.4 6 10 6C10.6 6 11 5.6 11 5ZM15 5V3C15 2.4 14.6 2 14 2C13.4 2 13 2.4 13 3V5C13 5.6 13.4 6 14 6C14.6 6 15 5.6 15 5ZM19 5V3C19 2.4 18.6 2 18 2C17.4 2 17 2.4 17 3V5C17 5.6 17.4 6 18 6C18.6 6 19 5.6 19 5Z" fill="currentColor"></path>
											<path d="M8.8 13.1C9.2 13.1 9.5 13 9.7 12.8C9.9 12.6 10.1 12.3 10.1 11.9C10.1 11.6 10 11.3 9.8 11.1C9.6 10.9 9.3 10.8 9 10.8C8.8 10.8 8.59999 10.8 8.39999 10.9C8.19999 11 8.1 11.1 8 11.2C7.9 11.3 7.8 11.4 7.7 11.6C7.6 11.8 7.5 11.9 7.5 12.1C7.5 12.2 7.4 12.2 7.3 12.3C7.2 12.4 7.09999 12.4 6.89999 12.4C6.69999 12.4 6.6 12.3 6.5 12.2C6.4 12.1 6.3 11.9 6.3 11.7C6.3 11.5 6.4 11.3 6.5 11.1C6.6 10.9 6.8 10.7 7 10.5C7.2 10.3 7.49999 10.1 7.89999 10C8.29999 9.90003 8.60001 9.80003 9.10001 9.80003C9.50001 9.80003 9.80001 9.90003 10.1 10C10.4 10.1 10.7 10.3 10.9 10.4C11.1 10.5 11.3 10.8 11.4 11.1C11.5 11.4 11.6 11.6 11.6 11.9C11.6 12.3 11.5 12.6 11.3 12.9C11.1 13.2 10.9 13.5 10.6 13.7C10.9 13.9 11.2 14.1 11.4 14.3C11.6 14.5 11.8 14.7 11.9 15C12 15.3 12.1 15.5 12.1 15.8C12.1 16.2 12 16.5 11.9 16.8C11.8 17.1 11.5 17.4 11.3 17.7C11.1 18 10.7 18.2 10.3 18.3C9.9 18.4 9.5 18.5 9 18.5C8.5 18.5 8.1 18.4 7.7 18.2C7.3 18 7 17.8 6.8 17.6C6.6 17.4 6.4 17.1 6.3 16.8C6.2 16.5 6.10001 16.3 6.10001 16.1C6.10001 15.9 6.2 15.7 6.3 15.6C6.4 15.5 6.6 15.4 6.8 15.4C6.9 15.4 7.00001 15.4 7.10001 15.5C7.20001 15.6 7.3 15.6 7.3 15.7C7.5 16.2 7.7 16.6 8 16.9C8.3 17.2 8.6 17.3 9 17.3C9.2 17.3 9.5 17.2 9.7 17.1C9.9 17 10.1 16.8 10.3 16.6C10.5 16.4 10.5 16.1 10.5 15.8C10.5 15.3 10.4 15 10.1 14.7C9.80001 14.4 9.50001 14.3 9.10001 14.3C9.00001 14.3 8.9 14.3 8.7 14.3C8.5 14.3 8.39999 14.3 8.39999 14.3C8.19999 14.3 7.99999 14.2 7.89999 14.1C7.79999 14 7.7 13.8 7.7 13.7C7.7 13.5 7.79999 13.4 7.89999 13.2C7.99999 13 8.2 13 8.5 13H8.8V13.1ZM15.3 17.5V12.2C14.3 13 13.6 13.3 13.3 13.3C13.1 13.3 13 13.2 12.9 13.1C12.8 13 12.7 12.8 12.7 12.6C12.7 12.4 12.8 12.3 12.9 12.2C13 12.1 13.2 12 13.6 11.8C14.1 11.6 14.5 11.3 14.7 11.1C14.9 10.9 15.2 10.6 15.5 10.3C15.8 10 15.9 9.80003 15.9 9.70003C15.9 9.60003 16.1 9.60004 16.3 9.60004C16.5 9.60004 16.7 9.70003 16.8 9.80003C16.9 9.90003 17 10.2 17 10.5V17.2C17 18 16.7 18.4 16.2 18.4C16 18.4 15.8 18.3 15.6 18.2C15.4 18.1 15.3 17.8 15.3 17.5Z" fill="currentColor"></path>
										</svg>
									</span>
									<Flatpickr data-enable-time {...formik.getFieldProps("judgingOpeningDate")}
										placeholder="Select a date"
										className={clsx(
											'form-control form-control-solid ps-12 mb-lg-0 flatpickr-input',
											{ 'is-invalid': formik.touched.judgingOpeningDate && formik.errors.judgingOpeningDate },
											{
												'is-valid': formik.touched.judgingOpeningDate && !formik.errors.judgingOpeningDate,
											}
										)}
										options={sharedDatePickerOptions}
										value={formik.values.judgingOpeningDate}
										onChange={(val: any) => {
											const date: Date = new Date(val[0])
											if (!isNaN(date.getTime()))
												formik.setFieldValue("judgingOpeningDate", moment(date).format("YYYY-MM-DDTHH:mm:ss"))
											else
												formik.setFieldValue("judgingOpeningDate", null)

										}}
										disabled={formik.isSubmitting || award.isClosed}
									/>
								</div>
							</div>
							<div className="col-md-6 fv-row">
								<label className="required fs-6 fw-semibold mb-2" htmlFor="modal_submissions_edit_award_judging_close">Judging Closing Date</label>
								<div className="position-relative d-flex align-items-center">
									<span className="svg-icon svg-icon-2 position-absolute mx-4">
										<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="currentColor"></path>
											<path d="M6 6C5.4 6 5 5.6 5 5V3C5 2.4 5.4 2 6 2C6.6 2 7 2.4 7 3V5C7 5.6 6.6 6 6 6ZM11 5V3C11 2.4 10.6 2 10 2C9.4 2 9 2.4 9 3V5C9 5.6 9.4 6 10 6C10.6 6 11 5.6 11 5ZM15 5V3C15 2.4 14.6 2 14 2C13.4 2 13 2.4 13 3V5C13 5.6 13.4 6 14 6C14.6 6 15 5.6 15 5ZM19 5V3C19 2.4 18.6 2 18 2C17.4 2 17 2.4 17 3V5C17 5.6 17.4 6 18 6C18.6 6 19 5.6 19 5Z" fill="currentColor"></path>
											<path d="M8.8 13.1C9.2 13.1 9.5 13 9.7 12.8C9.9 12.6 10.1 12.3 10.1 11.9C10.1 11.6 10 11.3 9.8 11.1C9.6 10.9 9.3 10.8 9 10.8C8.8 10.8 8.59999 10.8 8.39999 10.9C8.19999 11 8.1 11.1 8 11.2C7.9 11.3 7.8 11.4 7.7 11.6C7.6 11.8 7.5 11.9 7.5 12.1C7.5 12.2 7.4 12.2 7.3 12.3C7.2 12.4 7.09999 12.4 6.89999 12.4C6.69999 12.4 6.6 12.3 6.5 12.2C6.4 12.1 6.3 11.9 6.3 11.7C6.3 11.5 6.4 11.3 6.5 11.1C6.6 10.9 6.8 10.7 7 10.5C7.2 10.3 7.49999 10.1 7.89999 10C8.29999 9.90003 8.60001 9.80003 9.10001 9.80003C9.50001 9.80003 9.80001 9.90003 10.1 10C10.4 10.1 10.7 10.3 10.9 10.4C11.1 10.5 11.3 10.8 11.4 11.1C11.5 11.4 11.6 11.6 11.6 11.9C11.6 12.3 11.5 12.6 11.3 12.9C11.1 13.2 10.9 13.5 10.6 13.7C10.9 13.9 11.2 14.1 11.4 14.3C11.6 14.5 11.8 14.7 11.9 15C12 15.3 12.1 15.5 12.1 15.8C12.1 16.2 12 16.5 11.9 16.8C11.8 17.1 11.5 17.4 11.3 17.7C11.1 18 10.7 18.2 10.3 18.3C9.9 18.4 9.5 18.5 9 18.5C8.5 18.5 8.1 18.4 7.7 18.2C7.3 18 7 17.8 6.8 17.6C6.6 17.4 6.4 17.1 6.3 16.8C6.2 16.5 6.10001 16.3 6.10001 16.1C6.10001 15.9 6.2 15.7 6.3 15.6C6.4 15.5 6.6 15.4 6.8 15.4C6.9 15.4 7.00001 15.4 7.10001 15.5C7.20001 15.6 7.3 15.6 7.3 15.7C7.5 16.2 7.7 16.6 8 16.9C8.3 17.2 8.6 17.3 9 17.3C9.2 17.3 9.5 17.2 9.7 17.1C9.9 17 10.1 16.8 10.3 16.6C10.5 16.4 10.5 16.1 10.5 15.8C10.5 15.3 10.4 15 10.1 14.7C9.80001 14.4 9.50001 14.3 9.10001 14.3C9.00001 14.3 8.9 14.3 8.7 14.3C8.5 14.3 8.39999 14.3 8.39999 14.3C8.19999 14.3 7.99999 14.2 7.89999 14.1C7.79999 14 7.7 13.8 7.7 13.7C7.7 13.5 7.79999 13.4 7.89999 13.2C7.99999 13 8.2 13 8.5 13H8.8V13.1ZM15.3 17.5V12.2C14.3 13 13.6 13.3 13.3 13.3C13.1 13.3 13 13.2 12.9 13.1C12.8 13 12.7 12.8 12.7 12.6C12.7 12.4 12.8 12.3 12.9 12.2C13 12.1 13.2 12 13.6 11.8C14.1 11.6 14.5 11.3 14.7 11.1C14.9 10.9 15.2 10.6 15.5 10.3C15.8 10 15.9 9.80003 15.9 9.70003C15.9 9.60003 16.1 9.60004 16.3 9.60004C16.5 9.60004 16.7 9.70003 16.8 9.80003C16.9 9.90003 17 10.2 17 10.5V17.2C17 18 16.7 18.4 16.2 18.4C16 18.4 15.8 18.3 15.6 18.2C15.4 18.1 15.3 17.8 15.3 17.5Z" fill="currentColor"></path>
										</svg>
									</span>
									<Flatpickr data-enable-time {...formik.getFieldProps("judgingClosingDate")}
										placeholder="Select a date"
										className={clsx(
											'form-control form-control-solid ps-12 mb-lg-0 flatpickr-input',
											{ 'is-invalid': formik.touched.judgingClosingDate && formik.errors.judgingClosingDate },
											{
												'is-valid': formik.touched.judgingClosingDate && !formik.errors.judgingClosingDate,
											}
										)}
										options={sharedDatePickerOptions}
										value={formik.values.judgingClosingDate}
										onChange={(val: any) => {
											const date: Date = new Date(val[0])
											if (!isNaN(date.getTime()))
												formik.setFieldValue("judgingClosingDate", moment(date).format("YYYY-MM-DDTHH:mm:ss"))
											else
												formik.setFieldValue("judgingClosingDate", null)
										}}
										disabled={formik.isSubmitting || award.isClosed}
									/>
								</div>
							</div>
							<div className="col-md-6 fv-row">
								{formik.touched.judgingOpeningDate && formik.errors.judgingOpeningDate && (

									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role="alert" >
												<>{formik.errors.submissionOpeningDate}</>
											</span>
										</div>
									</div>

								)}
							</div>
							<div className="col-md-6 fv-row">
								{formik.touched.judgingClosingDate && formik.errors.judgingClosingDate && (

									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role="alert" >
												<>{formik.errors.judgingClosingDate}</>
											</span>
										</div>
									</div>

								)}
							</div>
						</div>
						<div className="row g-9">
							<div className="col-md-12">
								<NotificationBanner message="All dates use the UK timezone (GMT/BST)" />
							</div>
						</div>
						<div className="row g-9 mb-8">
							<div className="col-md-6 fv-row">
								<label className="fs-6 fw-semibold mb-2">Judging Notifications Sent</label>
								<div className="position-relative d-flex align-items-center">
									<label>{awardForEdit.judgingNotificationsDate}</label>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer justify-content-between" id="kt_modal_submissions_edit_award_details_footer">
					<div className="mt-4 mt-md-0 order-2 order-md-0">
						{award.id !== 0 &&

							<button onClick={deleteOnClick} type="button" id="kt_modal_submissions_edit-award-details_delete" className="btn btn-light btn-color-gray-700 btn-active-color-danger">Delete award</button>

						}
					</div>
					<div>
						<button type="button" id="kt_modal_edit-award-cancel"
							data-bs-dismiss="modal"
							onClick={cancel}
							className="btn btn-light btn-color-gray-700 btn-active-color-info me-3"
							disabled={formik.isSubmitting}
						>
							Cancel
						</button>
						{!award.isClosed &&
							<button type="submit" id="kt_modal_edit-award-details_submit"
								className="btn btn-info"
								disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
							>
								<span className="indicator-label">Save and close</span>
								{formik.isSubmitting && (
									<span className='indicator-progress'>
										Please wait...{' '}
										<span className='spinner-border spinner-border-sm align-middle ms-2'></span>
									</span>
								)}</button>
						}

					</div>
				</div>
			</form>
			{formik.isSubmitting && <LoadingComponent />}
		</ModalContainer>
	)
}

export { AwardSettingsForm }